/**
 * The graph half of the pipeline: AST -> flow graph -> layout -> routing.
 *
 * Three passes, deliberately kept separate and separately testable. Each one
 * takes a `FlowGraph` and returns a richer `FlowGraph`; none of them knows
 * anything about React or the DOM.
 */
import type { Program } from '../language/ast/nodes';
import type { SourceRange } from '../language/ast/source';
import type { SemanticInfo } from '../language/semantic/analyze';
import type { CompileOptions } from './compiler/compile';
import { compileProgram, DEFAULT_COMPILE_OPTIONS } from './compiler/compile';
import type { LayoutOptions } from './layout/layout';
import { layoutGraph, DEFAULT_LAYOUT_OPTIONS } from './layout/layout';
import type { RoutingOptions } from './routing';
import { routeGraph } from './routing';
import type { FlowGraph } from './model/types';
import { computeFullBounds } from './model/types';

export * from './model/types';
export { measureNode, measureText, wrapText, DEFAULT_SIZING } from './model/measure';
export type { SizingOptions, MeasuredLabel } from './model/measure';
export { compileProgram, DEFAULT_COMPILE_OPTIONS } from './compiler/compile';
export type { CompileOptions, CompileResult } from './compiler/compile';
export { layoutGraph, DEFAULT_LAYOUT_OPTIONS } from './layout/layout';
export type { LayoutOptions } from './layout/layout';
export * from './routing';

export interface DiagramOptions {
  readonly compile?: Partial<CompileOptions>;
  readonly layout?: Partial<LayoutOptions>;
  readonly routing?: Partial<RoutingOptions>;
}

export interface DiagramResult {
  readonly graph: FlowGraph;
  /** Node id -> source range, so clicking a node can reveal its code. */
  readonly nodeRanges: ReadonlyMap<string, SourceRange>;
}

export const DEFAULT_DIAGRAM_OPTIONS: Required<DiagramOptions> = {
  compile: DEFAULT_COMPILE_OPTIONS,
  layout: DEFAULT_LAYOUT_OPTIONS,
  routing: {},
};

/**
 * Runs the whole graph pipeline.
 *
 * `bounds` on the returned graph covers the routed edges as well as the nodes,
 * because a back edge that loops out to the side is part of what the viewport
 * has to fit.
 */
export function buildDiagram(
  program: Program,
  semantics: SemanticInfo,
  options: DiagramOptions = {},
): DiagramResult {
  const direction = options.compile?.direction ?? options.layout?.direction;

  const { graph, nodeRanges } = compileProgram(program, semantics, {
    ...options.compile,
    ...(direction ? { direction } : {}),
  });

  const laid = layoutGraph(graph, {
    ...options.layout,
    ...(direction ? { direction } : {}),
  });

  const routed = routeGraph(laid, options.routing);

  return {
    graph: { ...routed, bounds: computeFullBounds(routed) },
    nodeRanges,
  };
}
